import type { InteractionFinding } from './interactions';
import { checkSampleRatio, type VariantAllocation } from 'helpers/sample-ratio';
import { isPreStart } from 'helpers/experiment-status';

/**
 * What still needs a human, derived from current state.
 *
 * Deliberately not derived from the notification feed. Notifications are an
 * event stream with per-user read state: they answer "what happened". This
 * answers "what is still true and unresolved", and the two diverge the moment
 * someone clears a badge. If the queue were built from notifications, marking
 * one read would delete the work item and a winner would quietly never ship.
 *
 * Everything here is pure so the rules can be tested without a database. The
 * handler's only job is to load the inputs.
 */

export type AttentionKind =
  | 'interaction'
  | 'srm'
  | 'no-data'
  | 'overdue'
  | 'significant-positive'
  | 'significant-negative'
  | 'no-effect'
  | 'needs-implementation'
  | 'deployed-not-implemented'
  | 'needs-report'
  | 'next-by-pxl';

/**
 * Broken beats decide beats queue.
 *
 * An experiment with a broken traffic split may also be reporting a significant
 * winner. Showing both would invite someone to act on the winner, so the trust
 * failure suppresses the result rather than sitting beside it.
 */
export type AttentionSeverity = 'broken' | 'decide' | 'queue';

export const SEVERITY_OF: Record<AttentionKind, AttentionSeverity> = {
  interaction: 'broken',
  srm: 'broken',
  'no-data': 'broken',
  overdue: 'decide',
  'significant-positive': 'decide',
  'significant-negative': 'decide',
  'no-effect': 'decide',
  'needs-implementation': 'queue',
  'deployed-not-implemented': 'queue',
  'needs-report': 'queue',
  'next-by-pxl': 'queue',
};

const SEVERITY_ORDER: Record<AttentionSeverity, number> = { broken: 0, decide: 1, queue: 2 };

/**
 * Structured, not rendered. Mirrors how notification events store payloads: the
 * same item has to become a card row now and possibly a digest email later, and
 * a sentence frozen at query time cannot be re-linked when a route changes.
 */
export type AttentionItem = {
  kind: AttentionKind;
  severity: AttentionSeverity;
  experimentId: number;
  experimentName: string;
  data: Record<string, unknown>;
};

export type AttentionVariant = {
  id: number;
  name: string;
  weight: number | null;
  deployed: number | null;
};

export type AttentionExperiment = {
  id: number;
  name: string;
  status: string;
  /** When it started. Optional so callers that never start-date their experiments keep compiling. */
  startsAt?: number | null;
  endsAt: number | null;
  implementedAt: number | null;
  pxlScores: unknown;
  variants: AttentionVariant[];
};

/** Per-variant figures out of the stored statistics payload. */
export type VariantFigures = { sessions: number; improvement: number | null };

export type ExperimentSignals = {
  /** variantId -> figures, from statisticsData under the all-currency bucket. */
  variants: Record<string, VariantFigures>;
  /** variantId -> verdict, from significanceData under the pinned policy. */
  significance: Record<string, SignificanceFigures>;
  /** Sessions recorded in the last 24 hours, across all variants. */
  recentSessions: number;
};

export type SignificanceFigures = {
  variantName: string;
  significant: boolean;
  zScore: number | null;
  pValue: number | null;
  sprtResult: string | null;
  liftLower: number | null;
  liftUpper: number | null;
};

export type AttentionInput = {
  now: number;
  experiments: readonly AttentionExperiment[];
  signals: ReadonlyMap<number, ExperimentSignals>;
  /**
   * Whether anything in this batch recorded traffic in the window.
   *
   * A single experiment with no visitors is broken. Every experiment with no
   * visitors is an ingestion outage, and blaming each one individually turns
   * one incident into two hundred false alarms that bury whatever is genuinely
   * wrong. When this is false the no-data rule is suppressed, because the data
   * cannot distinguish the two cases and the confident answer is the wrong one.
   *
   * Defaults to true so a caller with a single experiment keeps the check.
   */
  trafficObserved?: boolean;
  /**
   * Experiments currently interfering with another, by experiment id.
   *
   * Precomputed by the caller for the same reason as signals: deciding this needs each
   * variant's changes and each URL targeting row, which the attention query does not load
   * and should not, since it would multiply a small request by every change on the site.
   *
   * Defaults to empty, so a caller that has not loaded enough to answer the question
   * reports nothing rather than a confident no.
   */
  interactions?: ReadonlyMap<number, InteractionFinding>;
  /**
   * Experiments whose debrief has at least one answer, by experiment id.
   *
   * Precomputed by the caller for the same reason as the rest: the question lives in another
   * table. Left out, the debrief rule says nothing rather than asking everyone to write one,
   * so a caller that could not load it (a failed query) degrades to no nudge, never to a flood.
   */
  reported?: ReadonlySet<number>;
};

/**
 * A running experiment with no traffic at all in a full day is not a slow
 * experiment, it is a broken one: a targeting rule that stopped matching, a
 * snippet removed on deploy, a tracking outage.
 */
const NO_DATA_WINDOW_MS = 24 * 60 * 60 * 1000;

/**
 * How long a rollout may sit unimplemented before it is worth raising.
 *
 * A deployed variant still runs through Pertento, so nothing is broken; it is
 * just a change that should be in the codebase and is not. Thirty days makes it
 * a reminder rather than nagging.
 */
const DEPLOYED_GRACE_MS = 30 * 24 * 60 * 60 * 1000;

/**
 * Only experiments that ended at or after this instant are asked for a debrief.
 *
 * The feature went live on 2026-10-06 at about 12:30 UTC, when `experiment_reports` was created.
 * Asking for a debrief on a test that ended last spring would put every old experiment in the
 * queue at once, for a note nobody could write well from memory; they can still be written from
 * the Debrief tab. A fixed instant, not "N days ago", so the set does not slide as time passes.
 */
const REPORT_PROMPT_FROM_MS = Date.UTC(2026, 9, 6, 12, 30);

/** Matches the significance detector: below this, a crossing is noise. */
const MIN_SESSIONS_PER_ARM = 1000;

type PxlScores = { p: number; x: number; l: number };

const isScore = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value) && value > 0;

/** Mirrors the parser on the experiment info tab: partial scores do not count. */
export const parsePxlScores = (raw: unknown): PxlScores | null => {
  if (!raw || typeof raw !== 'object') return null;
  const { p, x, l } = raw as Partial<PxlScores>;
  if (!isScore(p) || !isScore(x) || !isScore(l)) return null;
  return { p, x, l };
};

/** Higher is better: impact and reach earn, effort costs. */
export const pxlTotal = (scores: PxlScores): number => scores.p + scores.x + scores.l;

const hasLiveDeployment = (experiment: AttentionExperiment): boolean =>
  experiment.variants.some((variant) => !!variant.deployed);

/**
 * The strongest verdict across variants, or null if none qualifies.
 *
 * Same gates as the notification detector, for the same reason: an arm short of
 * traffic makes any comparison premature, and a "significant" row whose lift
 * interval still spans zero reads as a result and is not one.
 */
export type Verdict = { kind: AttentionKind; variantId: string; figures: SignificanceFigures };

export function readVerdict(signals: ExperimentSignals): Verdict | null {
  const armSessions = Object.values(signals.variants).map((variant) => variant.sessions);
  if (!armSessions.length || armSessions.some((sessions) => sessions < MIN_SESSIONS_PER_ARM)) return null;

  let strongest: Verdict | null = null;
  let noEffect: Verdict | null = null;

  for (const [variantId, figures] of Object.entries(signals.significance)) {
    if (figures.sprtResult === 'Variant shows no effect' && !noEffect) {
      noEffect = { kind: 'no-effect', variantId, figures };
    }

    if (!figures.significant) continue;
    if (figures.zScore === null || figures.zScore === 0) continue;
    if (figures.liftLower === null || figures.liftUpper === null) continue;
    if (figures.liftLower <= 0 && figures.liftUpper >= 0) continue;

    const kind: AttentionKind = figures.zScore > 0 ? 'significant-positive' : 'significant-negative';
    if (!strongest || Math.abs(figures.zScore) > Math.abs(strongest.figures.zScore ?? 0)) {
      strongest = { kind, variantId, figures };
    }
  }

  if (strongest) return strongest;
  // Only reported when nothing crossed: "stop, it is flat" and "stop, it won"
  // are different actions and the winner is the one worth surfacing.
  return noEffect;
}

const item = (
  kind: AttentionKind,
  experiment: AttentionExperiment,
  data: Record<string, unknown> = {},
): AttentionItem => ({
  kind,
  severity: SEVERITY_OF[kind],
  experimentId: experiment.id,
  experimentName: experiment.name,
  data,
});

type ImplementationParams = {
  experiment: AttentionExperiment;
  experimentSignals: ExperimentSignals | undefined;
  now: number;
};

/** Whether a finished experiment's change still has to be built into the site. */
function implementationItem({ experiment, experimentSignals, now }: ImplementationParams): AttentionItem | null {
  if (experiment.implementedAt) return null;

  if (experiment.status === 'Deployed' && hasLiveDeployment(experiment)) {
    const deployedAt = Math.min(...experiment.variants.filter((v) => v.deployed).map((v) => v.deployed as number));
    if (now - deployedAt <= DEPLOYED_GRACE_MS) return null;
    return item('deployed-not-implemented', experiment, { deployedAt, days: daysBetween(deployedAt, now) });
  }

  if (experiment.status === 'Ended') {
    const verdict = experimentSignals ? readVerdict(experimentSignals) : null;
    if (verdict?.kind === 'significant-positive') {
      return item('needs-implementation', experiment, { variantName: verdict.figures.variantName });
    }
  }

  return null;
}

/**
 * When the experiment concluded, as best the stored state says.
 *
 * Ending stamps `endsAt`; deploying a winner sets the status and nothing else, so for a Deployed
 * experiment `endsAt` is whatever the schedule said and not when it finished. The moment a variant
 * was deployed is the honest answer there.
 */
const concludedAt = (experiment: AttentionExperiment): number | null => {
  if (experiment.status === 'Ended') return experiment.endsAt;

  const deployed = experiment.variants.map((variant) => variant.deployed).filter((at): at is number => !!at);
  return deployed.length ? Math.min(...deployed) : null;
};

type DebriefParams = { experiment: AttentionExperiment; reported: ReadonlySet<number> | undefined };

/**
 * A test that finished after the debrief existed and has no answers on it.
 *
 * Nothing is stored for this: it is true while the debrief is empty and stops being true the moment
 * someone answers one question, with no read state to clear and no duplicate to suppress. Clearing
 * every answer brings it back, which is correct.
 */
function debriefItem({ experiment, reported }: DebriefParams): AttentionItem | null {
  if (!reported || reported.has(experiment.id)) return null;
  if (experiment.status !== 'Ended' && experiment.status !== 'Deployed') return null;

  const endedAt = concludedAt(experiment);
  if (endedAt === null || endedAt < REPORT_PROMPT_FROM_MS) return null;

  return item('needs-report', experiment, { endedAt });
}

/**
 * At most one item per experiment, plus one programme-level suggestion.
 *
 * One row per problem would let a single broken experiment fill the card and
 * push everything else off it, which is the failure mode that makes people stop
 * reading these lists.
 */
export function buildAttentionItems({
  now,
  experiments,
  signals,
  trafficObserved = true,
  interactions,
  reported,
}: AttentionInput): AttentionItem[] {
  const items: AttentionItem[] = [];

  for (const experiment of experiments) {
    const experimentSignals = signals.get(experiment.id);

    if (experiment.status === 'Running') {
      if (trafficObserved && experimentSignals && experimentSignals.recentSessions === 0) {
        items.push(item('no-data', experiment, { windowHours: NO_DATA_WINDOW_MS / (60 * 60 * 1000) }));
        continue;
      }

      /*
        The same problem, for an experiment that has never recorded anyone.

        Signals come from its experiment_stats row, and that row is only written once a
        first session has been processed. So an experiment whose snippet was never
        installed, or whose targeting never matched, had no signals and fell through every
        rule here: the one broken setup most likely in a new account was the one the card
        could not see. A full window since it started is the same bar the rule above uses.
      */
      const startedAt = experiment.startsAt ?? null;
      if (trafficObserved && !experimentSignals && startedAt !== null && now - startedAt >= NO_DATA_WINDOW_MS) {
        items.push(
          item('no-data', experiment, { windowHours: NO_DATA_WINDOW_MS / (60 * 60 * 1000), neverRecorded: true }),
        );
        continue;
      }

      /**
       * Ahead of the SRM check on purpose.
       *
       * An interaction is frequently the *cause* of the mismatch the next rule would
       * report: a redirect experiment moving traffic out of this one's audience shows up
       * here as a broken split with no explanation in it. Reporting the mismatch instead
       * would hand someone the symptom and hide the diagnosis. Behind no-data, though —
       * an experiment receiving nothing at all has a more basic problem than who it is
       * competing with.
       */
      const interaction = interactions?.get(experiment.id);
      if (interaction) {
        items.push(item('interaction', experiment, { ...interaction }));
        continue;
      }

      // Deployed variants are excluded: a rollout is not a randomised arm, so
      // the configured split does not predict its share.
      const allocations: VariantAllocation[] = experiment.variants
        .filter((variant) => !variant.deployed)
        .map((variant) => ({
          id: variant.id,
          name: variant.name,
          weight: variant.weight ?? 0,
          sessions: experimentSignals?.variants[String(variant.id)]?.sessions ?? 0,
        }));

      const ratio = checkSampleRatio(allocations);
      if (ratio.state === 'mismatch') {
        items.push(
          item('srm', experiment, {
            pValue: ratio.pValue,
            rows: ratio.rows.map((row) => ({
              name: row.name,
              actualShare: row.actualShare,
              expectedShare: row.expectedShare,
            })),
          }),
        );
        continue;
      }

      if (experiment.endsAt !== null && experiment.endsAt < now) {
        items.push(
          item('overdue', experiment, { endsAt: experiment.endsAt, daysOverdue: daysBetween(experiment.endsAt, now) }),
        );
        continue;
      }

      const verdict = experimentSignals ? readVerdict(experimentSignals) : null;
      if (verdict) {
        items.push(
          item(verdict.kind, experiment, {
            variantName: verdict.figures.variantName,
            pValue: verdict.figures.pValue,
            improvement: experimentSignals?.variants[verdict.variantId]?.improvement ?? null,
          }),
        );
      }
      continue;
    }

    // Not running. First whether the change got built, then whether anyone wrote down what was
    // learned. One item per experiment, so the debrief only asks when nothing more useful is owed.
    const owed = implementationItem({ experiment, experimentSignals, now }) ?? debriefItem({ experiment, reported });
    if (owed) items.push(owed);
  }

  const next = nextByPxl(experiments);
  if (next) items.push(next);

  return items.sort((a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity]);
}

/**
 * The highest-scoring draft, so the card answers "what next" and not only
 * "what went wrong". Only one, and only when a score exists: an unscored
 * backlog has nothing to rank and guessing an order defeats the point of PXL.
 */
function nextByPxl(experiments: readonly AttentionExperiment[]): AttentionItem | null {
  const scored = experiments
    .filter((experiment) => isPreStart(experiment.status))
    .map((experiment) => ({ experiment, scores: parsePxlScores(experiment.pxlScores) }))
    .filter((entry): entry is { experiment: AttentionExperiment; scores: PxlScores } => entry.scores !== null)
    .sort((a, b) => pxlTotal(b.scores) - pxlTotal(a.scores));

  const best = scored[0];
  if (!best) return null;

  return item('next-by-pxl', best.experiment, { scores: best.scores, total: pxlTotal(best.scores) });
}

const daysBetween = (from: number, to: number) => Math.floor((to - from) / (24 * 60 * 60 * 1000));

export { NO_DATA_WINDOW_MS, DEPLOYED_GRACE_MS, MIN_SESSIONS_PER_ARM, REPORT_PROMPT_FROM_MS };
